import axios, { AxiosResponse, AxiosRequestConfig } from "axios";
import qs from "querystring";
import moment from "moment";
import { message } from "antd";
import _ from "underscore";
import { IGroupQuestion } from "../pages/homework";
import groupByKeepOrder from "Utils/groupByKeepOrder";
import Config from "../config";
import { evaluationGroupType, evaluationStatus } from "./evaluate";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { getQiniuFileName } from "../Utils/filter";
import { GetMeetingListItemData } from "../pages/CourseDetail/Meeting/service";
import { logout } from "./auth";

axios.defaults.baseURL = process.env.REACT_APP_BaseUrl;

const RequestInterceptor = (config: AxiosRequestConfig) => {
    fingerprintJS();
    let token = localStorage.getItem("token");
    const batch = localStorage.getItem("batch");
    const fingerprint = localStorage.getItem("fingerprint");

    if (token) {
        config.headers = {
            token: token,
            batchNo: batch || Config.DefaultBatch,
            "Content-Type": "application/x-www-form-urlencoded",
            fingerprint: fingerprint,
            ...config.headers
        };
    }
    return config;
};

axios.interceptors.request.use(RequestInterceptor, error => {
    return Promise.reject(error);
});

axios.interceptors.response.use(
    response => {
        return response;
    },
    async error => {
        if (error.response && error.response.status === 401) {
            await logout();
        }
        if (
            error.response &&
            error.response.status === 400 &&
            error.response.data.code === "4000"
        )
            message.error(error.response.data.message);
        return Promise.reject(error);
    }
);

// MainRequest设计初衷是自定义错误信息, 而不是无论什么错误都用相同的方式提示
export const MainRequest = axios.create();

MainRequest.interceptors.request.use(RequestInterceptor, error => {
    return Promise.reject(error);
});

MainRequest.interceptors.response.use(
    response => {
        return response;
    },
    error => {
        if (error.response && error.response.status === 401) {
            logout();
        }

        return Promise.reject(error);
    }
);

// 由于上方拦截器烂熟写死form，为了适配json格式现代化请求，特此复制此对象，避免每个请求制定content-type
export const JsonRequest = axios.create();

const JsonRequestInterceptor = (config: AxiosRequestConfig) => {
    fingerprintJS();
    let token = localStorage.getItem("token");
    const batch = localStorage.getItem("batch");
    const fingerprint = localStorage.getItem("fingerprint");

    if (token) {
        config.headers = {
            token: token,
            batchNo: batch || Config.DefaultBatch,
            fingerprint: fingerprint,
            ...config.headers
        };
    }
    return config;
};
JsonRequest.interceptors.request.use(JsonRequestInterceptor, error => {
    return Promise.reject(error);
});

JsonRequest.interceptors.response.use(
    response => {
        return response;
    },
    error => {
        if (error.response && error.response.status === 401) {
            logout();
        }

        return Promise.reject(error);
    }
);

export const customerCode = "kb_dconline";

export enum StudentType {
    BenKeSheng = "1", // 本科
    YanJiuSheng = "2", // 研究生
    KuaJing = "3", // 跨境
    GaoZhi = "4", // 高职
    MBA = "5", // MBA
    BoShi = "6", // 博士
    GuoJi = "7", // 国际教育
    Teacher = "8" //老师
}

export const loginLog = async (userName: string) => {
    return axios
        .get("http://data.edufe.com.cn/data/kafka.php?action=login", {
            params: {
                userid: userName,
                customerCode
            }
        })
        .catch(err => err);
};

export const getName = () => {
    return axios.post("/student/getName").then(res => {
        localStorage.setItem("studentName", res.data.data.name);
        return res.data.data;
    });
};

export const getCurrentDate = () => {
    return axios
        .get("/student/currentDate")
        .then(res => res.data)
        .then(res => {
            res.data.theWeek =
                parseInt(res.data.theWeek) > 30
                    ? 30
                    : parseInt(res.data.theWeek);
            res.data.today = moment(res.data.today).format("YYYYMMDD");
            return res.data;
        });
};

export enum workState {
    NoHomework = "0", // 无作业
    HaveHomework = "1", // 有 未作
    Done = "2", // 已做 已批改
    NotMark = "3", // 已做 待批改
    Overtime = "4" // 未作过期
}

export enum liveState {
    NoIsLive = "0",
    IsLive = "1"
}

export interface CourseItem {
    courseId: string;
    address: string;
    teacherName: string;
    platformType: string;
    homeworkStatus: string;
    courseUrl: string;
    section: string;
    lmsCourseNo: string;
    type: string; //0:本科  1:研究生
    isPublish: string;
    score: string;
    courseName: string;
    isOpen: BooleanValue;
    dayOfWeek: string; // 星期几
    isLive: liveState;
    courseNo: string;
    weekNo: string; // 周次
    weekTimeId: number;
    courseSeq: string;
    continunity: string;
    channelId: string;
    signInId: string;
    signInStatus: BooleanValue; // 签到状态 不为'0'表示有签到
    votePaperId: string;
    votePaperStatus: BooleanValue;
    cousewareStudyStatus: string;
    haveExercise: BooleanValue; //判断是否有练习
    studentExerciseStatus: BooleanValue; // 是否有需要参与的练习 1有0无
    questionnaireStatus: BooleanValue; // 是否有问卷 1有0无
    isCloud: liveState; //是否是实验课
    beginTime: string;
    endTime: string;
    evaluationId: string;
    evaluationStatus: evaluationStatus;
    evaluationType: evaluationGroupType;
    meetingFlag: BooleanValue; //是否有会议
    meetingArray: GetMeetingListItemData[];
    meetingUrl: string;
}

export interface CourseDayItem {
    date: string;
    courseList: CourseItem[];
}

export interface CourseList {
    week: string;
    dayList: CourseDayItem[];
}

/**
 * 查询课表
 * @param weekNo
 * @param courseNo
 * @param courseSeq
 */
export const getCourseList = (
    weekNo?: number,
    courseNo?: string,
    courseSeq?: string
) => {
    return axios
        .get<SuccessData<CourseList[]>>("/student/queryCourseList", {
            params: { weekNo, courseNo, courseSeq }
        })
        .then(res => res.data.data);
};

/**
 * 查询课表并缓存
 */
export const getCourseListCache = (() => {
    let cache: CourseList[];
    let batch = window.localStorage.getItem("batch");

    return async () => {
        const currentBatch = window.localStorage.getItem("batch");
        if (!cache || batch !== currentBatch) cache = await getCourseList();
        batch = currentBatch;
        return cache;
    };
})();

/**
 * 获取单个课程信息
 * @param courseList 当前课程从第一周到最后一周全部数据
 * @param weekTimeId
 */
export const getCourseDetail = async (
    courseList: CourseList[],
    weekTimeId: number
) => {
    for (let week of courseList) {
        for (let day of week.dayList) {
            for (let course of day.courseList) {
                if (course.weekTimeId === weekTimeId)
                    return { ...course, date: day.date };
            }
        }
    }
    throw new Error("未查询到该课程信息");
};

/**
 * 获取单个课程信息
 * @param courseList 当前课程从第一周到最后一周全部数据
 * @param courseNo
 * @param courseSeq
 */
export const getCourseInfo = async (
    courseList: CourseList[],
    courseNo: string,
    courseSeq: string
) => {
    for (let week of courseList) {
        for (let day of week.dayList) {
            for (let course of day.courseList) {
                if (
                    course.courseNo === courseNo &&
                    course.courseSeq === courseSeq
                )
                    return course;
            }
        }
    }
    throw new Error("未查询到该课程信息");
};

/**
 * 获取平台名
 * @param type
 */
export function getPlatformName(type: string): string {
    switch (type) {
        case "1":
            return "白果云";
        default:
            return type || "未知平台";
    }
}

export const redirectKCZY = (lmsCourseNo: string) => {
    return axios
        .get(
            `/student/redirectKczy?userId=${localStorage.getItem(
                "user"
            )}&courseId=${lmsCourseNo}`
        )
        .then(res => res.data);
};

export interface IAnswer {
    answerId: number;
    content: string;
    isRight: "0" | "1";
    isChoose: "0" | "1";
    stuAnswer: string;
}

export interface ITopic {
    topicId: number;
    topicType: string;
    trunk: string;
    contentText: string | null;
    score: string;
    answers: IAnswer[];
    topics: ITopic[];
}

export interface IHomework {
    id: number;
    score: string;
    courseName: string;
    date: string;
    topics: ITopic[];
}

/**
 * 只取试卷学生端--试题列表接口
 * @param paperId
 */
export const getHomeworkPaper = (paperId: string) => {
    return axios
        .post<AxiosResponse<IHomework>>(
            `/student/paper`,
            qs.stringify({ paperId })
        )
        .then(res => res.data);
};

/**
 * 提交试题答案
 * @param courseNo
 * @param courseSeq
 * @param weekTime
 * @param week
 * @param section
 * @param paperId
 * @param topicType
 * @param topicId
 * @param parentId
 * @param weekTimeId
 * @param answers
 */
export const studentDo = (
    courseNo: string,
    courseSeq: string,
    weekTime: string,
    week: string,
    section: string,
    paperId: number,
    topicType: string,
    topicId: number,
    parentId: number | undefined,
    weekTimeId: string,
    answers: any[]
) => {
    return axios.post(
        `/student/studentDo`,
        qs.stringify({
            courseNo,
            courseSeq,
            weekTime,
            week,
            section,
            paperId,
            topicType,
            topicId,
            parentId,
            weekTimeId,
            answers: JSON.stringify(answers)
        }),
        {
            timeout: 10000,
            timeoutErrorMessage:
                "您的网络状态不佳，请刷新页面确认试题是否保存成功。"
        }
    );
};

/**
 * 学生提交试卷
 * @param params
 */
export const studentDoSubmit = (params: {
    paperId: number;
    weekTimeId: string;
}) => {
    return axios.post(`/student/stuSubmit`, qs.stringify(params));
};

interface IHomeworkHistory {
    paperId: number;
    studentId: number;
    score: string;
    objectiveScore: string;
    dannum: string;
    duonum: string;
    danRightnum: string;
    duoRightnum: string;
    danScore: string;
    duoScore: string;
    courseName: string;
    stuScore: string;
    date: string;
    topics: ITopic[];
}

/**
 * 获取历史试卷
 * @param paperId
 */
export const getHomeworkHistory = (paperId: string) => {
    return axios
        .post<AxiosResponse<IHomeworkHistory>>(
            `/student/stuPaper`,
            qs.stringify({ paperId })
        )
        .then(res => res.data);
};

/**
 * 获取作业数据(无论做过未做过)
 * @param homeworkState
 * @param props
 */
export async function getHomeworkData(
    homeworkState: string,
    props: {
        courseNo: string;
        courseSeq: string;
        weekTime: string;
        week: string;
        section: string;
        continunity: string;
        paperId: string;
    }
) {
    const examInfo = {
        courseName: "",
        week: props.week,
        section: props.section,
        courseNo: props.courseNo,
        courseSeq: props.courseSeq,
        weekTime: props.weekTime,
        date: ""
    };

    const HomeworkInfo: {
        id: number;
        score: string;
        count: number;
        stuScore: string;
        topics: ITopic[];
    } = {
        id: 0,
        score: "",
        count: 0,
        stuScore: "",
        topics: []
    };

    const examList: IGroupQuestion[] = [];

    if (homeworkState === "1") {
        const res = await getHomeworkPaper(props.paperId);

        const data = res.data;
        examList.push.apply(
            examList,
            groupByKeepOrder(data.topics, "topicType").map(item => {
                return { topicType: _.first(item)!.topicType, list: item };
            })
        );

        examInfo.courseName = data.courseName;
        examInfo.date = data.date;

        HomeworkInfo.id = data.id;
        HomeworkInfo.score = data.score;
        HomeworkInfo.count = data.topics.length;
        HomeworkInfo.topics = data.topics;
    } else {
        const res = await getHomeworkHistory(props.paperId);
        const data = res.data;
        let list: IGroupQuestion[] = groupByKeepOrder(
            data.topics,
            "topicType"
        ).map(item => {
            return { topicType: _.first(item)!.topicType, list: item };
        });

        examList.push.apply(examList, list);

        examInfo.courseName = data.courseName;
        examInfo.date = data.date;

        HomeworkInfo.score = data.score;
        HomeworkInfo.count = data.topics.length;
        HomeworkInfo.stuScore = data.stuScore;
        HomeworkInfo.topics = data.topics;
    }

    return { examList, examInfo, HomeworkInfo };
}

/**
 * 根据topicId查找父级, 多题干作业用
 * @param topicId
 * @param topics
 */
export function findParentIdByTopicId(
    topicId: number,
    topics: IGroupQuestion[]
): number | undefined {
    for (let group of topics) {
        for (let parent of group.list) {
            if (!parent.topics) continue;

            for (let topic of parent.topics) {
                if (topic.topicId === topicId) return parent.topicId;
            }
        }
    }

    return undefined;
}

export const fileUpload = (file: File) => {
    let formData = new FormData();
    formData.append("file", file);
    return axios.post(`/student/file/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
    });
};

export const getLoginCount = (studentNo: string) => {
    return axios
        .get(`/student/getLoginCount`, { params: { studentNo } })
        .then(res => res.data.data);
};

export interface HomeworkListItem {
    courseNo: string;
    courseSeq: string;
    courseName: string;
    week: string;
    weekTime: string;
    section: string;
    continunity: string;
    endDate: string;
    homeworkStatus: string;
    score: string;
    teacherName: string;
    paperId: string;
    weekTimeId: string;
    endTimeCode: null | "1";
}

export const getHomeworkList = () => {
    return axios
        .get<AxiosResponse<HomeworkListItem[]>>(
            "/student/HomeworkList?operation=3"
        )
        .then(res =>
            res.data.data.map(item => {
                item.week = (parseInt(item.week) - 1).toString();
                return item;
            })
        );
};

interface CurrentTime {
    dateTime: string;
    timestamp: number;
}

/**
 * 获取服务器时间
 */
export const getCurrentTime = () => {
    // return Promise.resolve({dateTime: moment().format("YYYY-MM-DD HH:mm:ss"), timestamp: 1588846812000})
    return axios
        .post<AxiosResponse<CurrentTime>>("/student/getCurrentTime")
        .then(res => res.data.data);
};

export enum ExamTimeType {
    Together = "1", // 统一时间
    Free = "2" // 学生自主安排
}

export interface ExamItem {
    examId: string;
    name: string;
    courseName: string;
    courseNo: string;
    courseSeq: string;
    type: string;
    startTime: string;
    endTime: string;
    examInfo: string;
    times: string;
    duration: string; // 考试时限。学生自主安排时, 考试时长, 默认120
    score: string; // 总分
    catchPhoto: BooleanValue; // 摄像头抓拍 1启用, 默认禁用
    realtimeVideo: BooleanValue; // 实时监控 1启用, 默认禁用
    timeType: ExamTimeType; // 开考时间 1统一时间, 2学生自主安排
    stuStartTime: string; // 学生开始作答时间
    fileNum: string; //附件数
    meetingRoom: string; //腾讯会议号
    allowPhoneUpload: BooleanValue;
}

/**
 * 获取考核列表
 */
export const getExamList = () => {
    return axios
        .post<AxiosResponse<ExamItem[]>>("/student/getExamList")
        .then(res => res.data.data);
};

export interface ExamPaperInfo {
    id: string;
    name: string;
    paperName: string; // 第几套试卷
    type: string;
    score: string;
    startTime: string;
    endTime: string;
    topics: ITopic[];
}

/**
 * 获取试卷详情
 * @param examId 考核ID
 */
export const getExamPaperInfo = (examId: string) => {
    return axios
        .post<AxiosResponse<ExamPaperInfo>>(
            "/student/getExamPaperInfo",
            qs.stringify({ examId })
        )
        .then(res => res.data.data);
};

export interface ExamSettings {
    submitTime: BooleanValue; // 是否提交 0 未提交
    seed: BooleanValue; // 试题是否乱序  0不乱序， 1乱序
    optionSeed: BooleanValue; // 选项是否乱序  0不乱序， 1乱序
    catchPhoto: BooleanValue; // 摄像头抓拍 1启用, 默认禁用
    startTime: string; // 试卷开始时间
    endTime: string; // 试卷结束时间
    timeType: string; // 开考时间 1统一时间, 2学生自主安排
    duration: string; // 考试时限。学生自主安排时, 考试时长, 默认120
    stuStartTime: string; // 学生开始作答时间
    realtimeVideo: BooleanValue; // 实时监控 1启用, 默认禁用
    ban: BooleanValue; // 实时监控 禁考 1禁止
    deviceIpInfo: BooleanValue; //考试进行中限制同时登录多设备   1限制
    phoneUploadFile: BooleanValue; //手机上传作答内容 0未点击, 1已点击上传作答内容
    allowPaperDownload: BooleanValue; //是否允许下载试卷
    examFlagInfo: string; //标记异常内容
    banInfo: string; //禁考原因
}

/**
 * 获取试卷设置
 * @param examId
 */
export function getExamSettings(examId: string, paperId?: string) {
    return axios
        .get<AxiosResponse<ExamSettings>>(
            `/student/examHeartbeat?${qs.stringify({ examId, paperId })}`
        )
        .then(res => res.data.data);
}

/**
 * 根据考核获取考场信息（心跳）
 * @param examId
 */
export function getMeetingRoomByInvigilation(examId: string) {
    return axios
        .get(`/student/getMeetingRoomByInvigilation?examId=${examId}`)
        .then(res => res.data.message);
}

export interface saveAnswerItem {
    answerId: number;
    content: string;
    attachment: null;
}

export const savePaperAnswer = (
    examId: string,
    paperId: string,
    topicId: number,
    answers: saveAnswerItem[],
    topicType?: string
) => {
    return axios
        .post<AxiosResponse>(
            "/student/savePaperAnswer",
            qs.stringify({
                examId,
                paperId,
                topicId,
                topicType,
                answers: JSON.stringify(answers)
            }),
            {
                timeout: 10000,
                timeoutErrorMessage:
                    "您的网络状态不佳，请刷新页面确认试题是否保存成功。"
            }
        )
        .then(res => res.data.data);
};

/**
 * 保存试卷
 * @param examId    考核id
 * @param paperId   卷子id
 */
export const submitPaper = (
    examId: string,
    paperId: string,
    answerList: string
) => {
    return axios
        .post(
            "/student/submitPaper",
            qs.stringify({ examId, paperId, answerList: answerList })
        )
        .then(res => res.data);
};

/**
 * 获取七牛上传token
 */
export const getQiniuUploadToken = () => {
    return (
        axios
            .post("/student/getUpToken")
            // return axios.get('http://172.16.20.23:8023/storage/get_token')
            .then(res => res.data)
    );
};

interface UploadParams {
    keepAttName?: boolean;
    onUploadProgress: (file: File, percent: number) => void;
}

/**
 * 图片上传到七牛
 */
export async function uploadToQiNiu(file: File, options?: UploadParams) {
    const request = axios.create();
    const token = await getQiniuUploadToken();
    const formData = new FormData();

    formData.append("file", file);
    formData.append("token", token.message);

    function onUploadProgress(progressEvent: any) {
        options?.onUploadProgress(
            file,
            (progressEvent.loaded * 100) / progressEvent.total
        );
    }

    return request
        .post<{ hash: string; key: string }>(Config.QiNiuUploadUrl, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress
        })
        .then(res => res.data)
        .then(data => {
            const { keepAttName = true } = options || {};
            const fileUrl = `${Config.QiNiuDownloadUrl}/${data.key}`;

            return {
                key: data.key,
                fileName: file.name,
                url: keepAttName ? `${fileUrl}?attname=${file.name}` : fileUrl
            };
        });
}

export const resetToken = (token: string, refreshToken: string) => {
    const headers = {
        token,
        refreshToken
    };
    return axios
        .get<AxiosResponse<{ token: string; type: string; studentNo: string }>>(
            "/student/resetToken",
            { headers }
        )
        .then(res => res.data.data);
};

export interface ExamTimeItem {
    id: number;
    courseNo: string;
    courseSeq: string;
    studentName: string;
    studentNo: string;
    examName: string;
    beginTime: string;
    department: string;
    className: string;
    openDeveloper?: string;
    courseName?: string;
    schoolZone: string;
    address?: any;
    teachingBuilding: string;
    classroom: string;
    seatNo: string;
    endTime: string;
    content?: string;
    batchNo: string;
    customerCode: string;
}

/**
 * 获取学生考核消息通知
 */
export const getExamTime = () => {
    return axios
        .post<SuccessData<ExamTimeItem[]>>("/student/getExamTime")
        .then(res => res.data.data);
};

/**
 * 保存学员离开试卷次数
 */
export function timeCheck(examId: string, paperId?: string) {
    return axios.post("/student/timeCheck", qs.stringify({ examId, paperId }));
}

/**
 * 保存学员多显示器提醒
 * @param examId
 * @param paperId
 */
export function saveMultiScreen(examId: string, paperId?: string) {
    return axios.post(
        "/student/multiScreen",
        qs.stringify({ examId, paperId })
    );
}

/**
 * 保存下载附件记录
 * @param examId
 * @param paperId
 * @param fileUrl
 */
export function saveExamFileDownloadRecord(
    examId: string,
    paperId: string,
    fileUrl: string
) {
    const fileName = getQiniuFileName(fileUrl);

    return axios.post(
        "/student/saveExamFileDownloadRecord",
        qs.stringify({ examId, paperId, fileUrl, fileName })
    );
}

/**
 * 保存人脸核验照片
 */
export function faceCheck(examId: string, url: string, paperId?: string) {
    return axios.post(
        "/student/faceCheck",
        qs.stringify({ examId, url, paperId })
    );
}

/**
 * dataUrl to file object
 * @param dataUrl
 * @param filename
 */
export function dataURLtoFile(dataUrl: string, filename: string) {
    let arr = dataUrl.split(","),
        mime = arr[0].match(/:(.*?);/)?.[1],
        bStr = atob(arr[1]),
        n = bStr.length,
        u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bStr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
}

export enum ReadStatus {
    read = "1",
    unread = "0"
}

interface Message {
    id: string;
    title: string;
    content: string;
    createTime: string;
    readStatus: ReadStatus;
    createUser: string;
    updateTime: string;
}

/**
 * @name 获取通知列表
 * @description http://yapi.dufe.tech/project/82/interface/api/4761
 */
export const getMessageAll = () => {
    return axios
        .get<SuccessData<Message[]>>(`/student/message/getMessageAll`)
        .then(res => res.data.data);
};

/**
 * @name 获取消息详情
 * @param messageId 消息ID
 */
export const readMessage = (messageId: string) => {
    return axios
        .get<SuccessData<Message>>(`/student/message/readMessage`, {
            params: { messageId }
        })
        .then(res => res.data.data);
};

/**
 * 获取学生通知
 */

interface NoticeMessage {
    pageSize: string;
    pageNo: number;
    readFlag: number;
}

export interface NoticeListItem {
    id: number;
    title: string; // 标题
    content: string; // 内容
    studentNos?: string; // 学生
    createUser: string; // 创建的教师
    createTime: string; // 创建时间
    readStatus: string;
}

// 获取通知列表
export const getNoticeList = (
    pageSize: number,
    pageNo: number,
    readFlag: number
) => {
    return axios.get<NoticeMessage>("/student/message/getMessageAll", {
        params: {
            pageSize,
            pageNo,
            readFlag
        }
    });
};

// 获取单条标记为已读
export const setSingleRead = (messageId: string) => {
    return axios.get<NoticeMessage>("/student/message/readMessage", {
        params: { messageId }
    });
};

//获取学生未读个数
export const getUnreadNum = () => {
    return axios
        .get<SuccessData<number>>("/student/message/getNoReadMessageCount")
        .then(res => res.data.data);
};

//全部标记已读
export const markRead = () => {
    return axios.get("/student/message/updateAllReadStatus");
};

//清空已读
export const clearRead = () => {
    return axios.get("/student/message/clearReadOver");
};

interface ActivityStatus {
    signStatus: BooleanValue;
    votePaperStatus: BooleanValue;
    exerciseStatus: BooleanValue;
    coursewareLearnStatus: BooleanValue;
    questionnaireStatus: BooleanValue;
}
/**
 * 查询是否有进行中的活动(签到，投票)
 * @param weekTimeId
 */
export function getActivityByWeekTimeId(
    weekTimeId: number,
    courseNo?: string,
    courseSeq?: string,
    courseId?: string
) {
    return axios
        .post<SuccessData<ActivityStatus>>(
            "/student/getActivityByWeekTimeId",
            qs.stringify({ weekTimeId, courseNo, courseSeq, courseId })
        )
        .then(res => res.data.data);
}

export interface NoWeekTimeCourse {
    evaluationId: string;
    batchNo: string;
    courseName: string;
    isCloud: string;
    courseNo: string;
    courseSeq: string;
    className: string;
    lmsCourseNo: string;
    weekTimeId: string;
    teacherName: string;
    channelId: string; //频道号
    isOpen: string;
    evaluationType: evaluationGroupType;
    multiEvaluationFlag: "0" | "1";
    isDeputy: "0" | "1"; //是否评价主副老师
    evaluationStatus: evaluationStatus; //是否评教
}

/**
 * 计划课程
 * @param batchNo
 */
export function getNoWeekTimeCourseList(batchNo: string) {
    return axios
        .get<SuccessData<NoWeekTimeCourse[]>>(
            `/student/queryNoWeekTimeCourseList?batchNo=${batchNo}`
        )
        .then(res => res.data.data);
}

/**
 * 非计划课程
 * @param batchNo
 */
export function getNetCourseList(batchNo: string) {
    return axios
        .get<SuccessData<NoWeekTimeCourse[]>>(
            `/student/queryNetCourseList?batchNo=${batchNo}`
        )
        .then(res => res.data.data);
}

export interface SysMessage {
    id: string;
    title: string;
    content: string;
    createUser: string;
    createTime: string;
}

/**
 * 获取消息通知
 */
export const getSysMessage = () => {
    return axios
        .post<SuccessData<SysMessage[]>>("/student/getSysMessage")
        .then(res => res.data.data);
};

/**
 * base64转换为file文件流，以formdata的格式传给后端
 * @param dataBase64
 * @param filename
 */
export function base64toFile(dataBase64: string, filename = "file") {
    let arr = dataBase64.split(",");
    let mime = arr[0].match(/:(.*?);/)?.[1];
    let suffix = mime?.split("/")[1];
    const bstr = window.atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], `${filename}.${suffix}`, {
        type: mime
    });
}

/**
 * 手机点击上传作答内容调用此接口
 */
export const phoneClickUpload = (examId: string) => {
    return axios.post("/student/phoneClickUpload", qs.stringify({ examId }));
};

export interface StudentPhoneFileList {
    fileList: string[];
    size: number;
    fileNum: number; //学生提交文件数量, 包括手机端上传的和页面作答时候富文本中上传的
}

/**
 * 手机获取上传作答内容附件列表
 * @param flag="mobile"手机上传，否则就是PC上传的
 */
export const getStudentPhoneFileList = (examId: string, flag?: string) => {
    return axios
        .get<SuccessData<StudentPhoneFileList>>(
            "/student/getStudentPhoneFileList",
            { params: { examId, flag } }
        )
        .then(res => res.data.data);
};

/**
 * 手机上传作答内容单个附件保存
 * @param flag="mobile"手机上传，否则就是PC上传的
 */
export const savePhoneFile = (examId: string, file: string, flag?: string) => {
    return axios.post(
        "/student/savePhoneFile",
        qs.stringify({ examId, file, flag })
    );
};

/**
 * 手机上传作答内容单个附件删除
 *  @param flag="mobile"手机上传，否则就是PC上传的
 */
export const deletePhoneFile = (
    examId: string,
    file: string,
    flag?: string
) => {
    return axios.post(
        "/student/deletePhoneFile",
        qs.stringify({ examId, file, flag })
    );
};

interface ExamByIdInfo
    extends Omit<
        ExamSettings,
        "startTime" | "endTime" | "ban" | "deviceIpInfo" | "phoneUploadFile"
    > {
    examPaperId: string;
    examinfo: {
        courseName: string;
        courseNo: string;
        courseSeq: string;
        endTime: string;
        examId: string;
        name: string;
        startTime: string;
        type: string;
    };
}
/**
 * 获取当前考试信息，用于查询paperId
 */
export const getExamById = (examId: string) => {
    return axios
        .get<SuccessData<ExamByIdInfo>>("/student/getExamById", {
            params: { examId }
        })
        .then(res => res.data.data);
};

/**
 * 保存学员多显示器提醒
 */
export const multiScreen = (examId: string, paperId?: string) => {
    return axios.post(
        "/student/multiScreen",
        qs.stringify({ examId, paperId })
    );
};

function fingerprintJS() {
    FingerprintJS.load().then(fp => {
        fp.get().then(result => {
            const visitorId = result.visitorId;
            localStorage.setItem("fingerprint", visitorId);
        });
    });
}

fingerprintJS();

//分钟转换为天数
export function convertMinutesToDHM(minutes: number) {
    const days = Math.floor(minutes / 1440); // 一天有1440分钟
    const hours = Math.floor((minutes % 1440) / 60); // 一小时有60分钟
    const mine = minutes % 60;

    return { days, hours, mine };
}
//分钟转换为天数TEXT
export function convertMinutesToDHMText(
    minutes: number | string | null | undefined
) {
    if (minutes === "不限") {
        return minutes;
    }
    if (!minutes) {
        return "不限";
    }
    const { days, hours, mine } = convertMinutesToDHM(Number(minutes));

    let result = "";
    if (days > 0) {
        result += days + "天";
    }
    if (hours > 0) {
        result += hours + "小时";
    }
    if (mine > 0) {
        result += mine + "分钟";
    }

    return result;
}

export interface MessageItem {
    uuid: string;
    content: string;
    createTime: string;
    createUser: string;
    jumpUrl: string;
    readStatus: BooleanValue;
}

/**
 * 获取业务消息列表
 */
export function busiMessageList(unread: boolean) {
    return MainRequest.post<SuccessData<MessageItem[]>>(
        "/student/busiMessage/list",
        { readStatus: unread ? "0" : undefined },
        { headers: { "Content-Type": "application/json" } }
    ).then(res => res.data.data);
}

/**
 * 标记系统消息为已读
 * @param uuid
 */
export function busiMessageRead(uuid: string) {
    return MainRequest.get(`/student/busiMessage/read/${uuid}`);
}

/**
 * 标记所有系统消息为已读
 */
export function busiMessageReadAll() {
    return MainRequest.get("/student/busiMessage/readAll");
}

/**
 * 系统消息清除已读
 */
export function busiMessageClearRead() {
    return MainRequest.get("/student/busiMessage/clearRead");
}
